import { motion } from 'framer-motion'
import { Upload, Video, Brain, FileText, Zap, Shield, Clock, Users } from 'lucide-react'

const Benefits = () => {
  const benefits = [
    {
      icon: Upload,
      title: 'Automação Inteligente',
      description: 'Upload e análise automática de documentos com processamento em tempo real.',
      color: 'primary',
      features: ['Processamento automático', 'Múltiplos formatos', 'Validação inteligente']
    },
    {
      icon: Video,
      title: 'Transcrição de Vídeos',
      description: 'Transforme voz em texto com precisão superior a 95%. Ideal para você que precisa resultados confiáveis.',
      color: 'accent',
      features: ['Transcrição automática', 'Múltiplos idiomas', 'Sincronização de áudio']
    },
    {
      icon: Brain,
      title: 'IA com análise profunda e preditiva',
      description: 'TANDER SMART compreende sua necessidade independente da complexidade.',
      color: 'primary',
      features: ['Análise contextual', 'Múltiplos modelos', 'Aprendizado contínuo']
    },
    {
      icon: FileText,
      title: 'Geração de Relatórios',
      description: 'Relatórios em Word e Excel automáticos com formatação profissional.',
      color: 'accent',
      features: ['Templates personalizáveis', 'Exportação automática', 'Formatação profissional']
    }
  ]

  const stats = [
    { icon: Zap, value: '10x', label: 'Performance', color: 'accent' },
    { icon: Shield, value: '99.9%', label: 'Segurança', color: 'primary' },
    { icon: Clock, value: '<2s', label: 'Tempo Resposta', color: 'accent' },
    { icon: Users, value: '50k+', label: 'Usuários Ativos', color: 'primary' }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  }

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 }
  }

  return (
    <section id="funcionalidades" className="section-padding bg-white">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            TANDER SMART - Sua escolha certa e inteligente!
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Nossa plataforma combina tecnologia de ponta com simplicidade de uso para 
            revolucionar a forma como você analisa documentos.
          </p>
        </motion.div>

        {/* Stats */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              className="text-center"
            >
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-${stat.color}-100 text-${stat.color}-500 mb-4`}>
                <stat.icon className="w-8 h-8" />
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
              <div className="text-gray-600">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Benefits Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              whileHover={{ y: -10 }}
              className="card p-6 group"
            >
              <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl bg-${benefit.color}-100 text-${benefit.color}-500 mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <benefit.icon className="w-7 h-7" />
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {benefit.title}
              </h3>
              
              <p className="text-gray-600 mb-4 leading-relaxed">
                {benefit.description}
              </p>

              <ul className="space-y-2">
                {benefit.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center space-x-2 text-sm text-gray-600">
                    <div className={`w-2 h-2 rounded-full bg-${benefit.color}-400`}></div>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-primary-50 to-accent-50 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              <p>É AGORA!</p>
              Chegou a hora de colocar a IA TANDER SMART em seus documentos.
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Junte-se a muitos usuários que já estão economizando tempo e 
              obtendo insights valiosos com o TANDER SMART.
            </p>
            <motion.a
              href="#planos"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-primary"
            >
              Escolha seu Plano Inteligente
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default Benefits
